import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { MONTHS_SHORT } from "@/lib/i18n";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Film, Euro, Clock, Users, TrendingUp } from "lucide-react";

const Kpi = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="rounded-sm border border-neutral-200 bg-white p-6 transition-colors hover:border-black">
    <div className="flex items-center justify-between">
      <p className="text-xs font-semibold uppercase tracking-widest text-neutral-400">{label}</p>
      <Icon className="h-4 w-4 text-neutral-400" strokeWidth={1.75} />
    </div>
    <p className="mt-4 font-display text-3xl font-extrabold tracking-tight tabular-nums">{value}</p>
  </div>
);

export default function AdminHome() {
  const { t, lang, money } = useLang();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-neutral-400">{t("common.loading")}</div>;
  const { kpis } = stats;
  const short = MONTHS_SHORT[lang] || MONTHS_SHORT.fr;
  const monthly = stats.monthly.map((m) => ({ ...m, label: short[m.month - 1] || m.label }));

  return (
    <div className="ch-fade-up space-y-10">
      <header>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-neutral-400">{t("home.eyebrow")}</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight">{t("home.title")}</h1>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi testid="kpi-videos-edited" icon={Film} label={t("kpi.videos_edited")} value={kpis.videos_edited_this_month} />
        <Kpi testid="kpi-revenue" icon={Euro} label={t("kpi.revenue")} value={money(kpis.revenue_this_month)} />
        <Kpi testid="kpi-revenue-year" icon={TrendingUp} label={t("kpi.revenue_year")} value={money(kpis.revenue_this_year)} />
        <Kpi testid="kpi-videos-pending" icon={Clock} label={t("kpi.pending")} value={kpis.videos_pending} />
        <Kpi testid="kpi-clients" icon={Users} label={t("kpi.clients")} value={kpis.total_clients} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-sm border border-neutral-200 p-6">
          <h3 className="font-display text-lg font-bold">{t("chart.videos")}</h3>
          <div className="mt-6 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly}>
                <CartesianGrid vertical={false} stroke="#eee" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} stroke="#999" />
                <YAxis tickLine={false} axisLine={false} fontSize={12} stroke="#999" allowDecimals={false} />
                <Tooltip cursor={{ fill: "#f5f5f5" }} contentStyle={{ borderRadius: 4, border: "1px solid #000", fontSize: 12 }} />
                <Bar dataKey="videos" fill="#000" radius={[2, 2, 0, 0]} maxBarSize={28} name={t("common.videos")} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-sm border border-neutral-200 p-6">
          <h3 className="font-display text-lg font-bold">{t("chart.revenue")}</h3>
          <div className="mt-6 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthly}>
                <CartesianGrid vertical={false} stroke="#eee" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} stroke="#999" />
                <YAxis tickLine={false} axisLine={false} fontSize={12} stroke="#999" />
                <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 4, border: "1px solid #000", fontSize: 12 }} />
                <Line type="monotone" dataKey="revenue" stroke="#000" strokeWidth={2} dot={{ r: 3, fill: "#000" }} name={t("kpi.revenue")} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-sm border border-neutral-200 p-6 lg:col-span-2">
          <h3 className="font-display text-lg font-bold">{t("chart.orders")}</h3>
          <div className="mt-6 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly}>
                <CartesianGrid vertical={false} stroke="#eee" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} stroke="#999" />
                <YAxis tickLine={false} axisLine={false} fontSize={12} stroke="#999" allowDecimals={false} />
                <Tooltip cursor={{ fill: "#f5f5f5" }} contentStyle={{ borderRadius: 4, border: "1px solid #000", fontSize: 12 }} />
                <Bar dataKey="orders" fill="#a3a3a3" radius={[2, 2, 0, 0]} maxBarSize={28} name={t("nav.orders")} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
